import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AbstractCrudApiService } from '@shared/abstracts/abstract-crud-api.service';
import { urlJoin } from '@shared/utils/url.utils';

export abstract class AbstractUserService<Model, CreateDto, UpdateDto> extends AbstractCrudApiService<Model, CreateDto, UpdateDto> {

  override get endpoint(): string {
    return `${this.api$.value}/:userId`;
  }

  protected constructor(
  ) {
    super();
    this.api$.next(urlJoin(this.env.get('api.base'), 'users'));
    this.logger.debug(this.name, 'Endpoint', this.endpoint);
  }

  /**
   * True when an account already holds this email — i.e. `true` means the address
   * is NOT available. The check spans the whole platform and counts soft-deleted
   * accounts, matching the UNIQUE constraint the save would hit, so this endpoint
   * and the 400 the save would return cannot disagree.
   */
  validateEmail(email: string, id?: number | null): Observable<boolean> {
    return this.http.get<boolean>(`${this.url()}/validate-email`, {
      params: this.uniquenessParams('email', email, id),
    });
  }

  /** Same contract as {@link validateEmail}, for the fiscal code. */
  validateFiscalCode(fiscalCode: string, id?: number | null): Observable<boolean> {
    return this.http.get<boolean>(`${this.url()}/validate-fiscal-code`, {
      params: this.uniquenessParams('fiscalCode', fiscalCode, id),
    });
  }

  /**
   * `id` excludes the account being edited, so keeping one's own email or fiscal
   * code is not reported as a conflict. It is omitted while creating — there is no
   * record to exclude yet, and the backend rejects a non-positive id.
   */
  private uniquenessParams(key: string, value: string, id?: number | null): HttpParams {
    const params = new HttpParams().set(key, value);
    return id != null && id > 0 ? params.set('id', id) : params;
  }
}
